import { Modal, Table, Tag, Typography } from "antd";
import { Box, Package, X } from "lucide-react";
import { FC, useMemo } from "react";
import { useGetInventoriesInfinite } from "~/services/inventory";
import { TInventory } from "~/types";
import { calculateCubicVolume, capitalize, cn, formatPackingType } from "~/utils";
import {
  CheckType,
  InventoryStatusCode,
  InventoryType,
} from "~/utils/constants";
import { useDetailBookingStore } from "./detail-booking.store";

const { Text } = Typography;

const getStatusColor = (status: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "green";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "blue";
    case InventoryStatusCode.NO_POSSESSION:
      return "orange";
    case InventoryStatusCode.RELEASED:
      return "purple";
    default:
      return "default";
  }
};

const calculateCubicFeet = (inventory: TInventory) => {
  if (
    inventory.length != null &&
    inventory.width != null &&
    inventory.height != null &&
    inventory.length > 0 &&
    inventory.width > 0 &&
    inventory.height > 0
  ) {
    const cubicFeet = calculateCubicVolume(inventory) / 1728;
    return cubicFeet.toFixed(2);
  }
  return "N/A";
};

export const ViewInventoryModal: FC = () => {
  const { booking, showInventoryModal, setShowInventoryModal } = useDetailBookingStore();

  const {
    data: inventoriesData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useGetInventoriesInfinite({
    checkParentId: CheckType.CHECK,
    pageSize: 20,
    clientId: booking?.clientId ?? "",
  });

  const inventories = useMemo(() => {
    return inventoriesData?.pages.flatMap((page) => page.items) ?? [];
  }, [inventoriesData]);

  const columns = [
    {
      title: "Type",
      dataIndex: "itemType",
      key: "itemType",
      width: 100,
      render: (itemType: InventoryType) => {
        const Icon = itemType === InventoryType.PACKAGE ? Box : Package;
        return (
          <div className="flex items-center">
            <Icon
              size={16}
              className={cn(
                itemType === InventoryType.PACKAGE
                  ? "text-amber-500"
                  : "text-green-500",
                "mr-2"
              )}
            />
            <span>{capitalize(itemType)}</span>
          </div>
        );
      },
    },
    {
      title: "ID",
      dataIndex: "inventoryRef",
      key: "inventoryRef",
      width: 120,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: "Artist",
      dataIndex: "artistName",
      key: "artistName",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: InventoryStatusCode) => (
        <Tag color={getStatusColor(status)}>
          {capitalize(status.replace(/_/g, " "))}
        </Tag>
      ),
    },
    {
      title: "Location",
      dataIndex: ["location", "name"],
      key: "location",
      width: 150,
      render: (locationName: string) => locationName || "N/A",
    },
    {
      title: "Packing",
      key: "packingType",
      width: 150,
      render: (_: unknown, record: TInventory) => formatPackingType(record),
    },
    {
      title: "Cubic Feet",
      key: "cubicFeet",
      width: 100,
      render: (_: any, record: TInventory) => calculateCubicFeet(record),
    },
  ];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

    if (scrolledToBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Modal
      open={showInventoryModal}
      onCancel={() => setShowInventoryModal(false)}
      width={1200}
      footer={null}
      closeIcon={<X size={20} />}
      title={
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Client Inventory</h2>
          <Text type="secondary">
            {inventories.length} {inventories.length === 1 ? "item" : "items"}
          </Text>
        </div>
      }
    >
      <div
        onScroll={handleScroll}
        style={{
          height: 500,
          overflow: "auto",
        }}
      >
        <Table
          columns={columns}
          dataSource={inventories}
          rowKey="id"
          pagination={false}
          loading={isLoading && !isFetchingNextPage}
          size="small"
        />
        {isFetchingNextPage && (
          <div className="text-center p-4">Loading more...</div>
        )}
        {!hasNextPage && inventories.length > 0 && (
          <div className="text-center p-4 text-gray-500">
            All items loaded
          </div>
        )}
      </div>
    </Modal>
  );
};
